import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { notifications } from "@mantine/notifications";
import { labsQueryKey, surgeryCentersQueryKey } from "../useReferenceData";
import {
  surgeryCentersLabsService,
  CreateSurgeryCenterLabInput,
  UpdateSurgeryCenterLabInput,
  CreateSurgeryCenterLabReportDeliveryMethodInput,
  UpdateSurgeryCenterLabReportDeliveryMethodInput,
  SurgeryCenterLab,
  SurgeryCenterPhysicianLink,
} from "../../services/admin/surgeryCentersLabs.service";

export type { SurgeryCenterLab, SurgeryCenterPhysicianLink };

// Backend rejects create/update with HTTP 409 + { error: "NO_PORTAL_ACCESS" }
// when the selected method is Portal but the location has no active portal
// grants. The inline alert in the modal handles that case, so the toast is
// suppressed for it.
function isNoPortalAccessError(error: unknown): boolean {
  const response = (
    error as { response?: { status?: number; data?: { error?: string } } }
  )?.response;
  return (
    response?.status === 409 && response?.data?.error === "NO_PORTAL_ACCESS"
  );
}

export function useSurgeryCentersLabsAdmin() {
  return useQuery({
    queryKey: ["admin", "surgery-centers-labs"],
    queryFn: surgeryCentersLabsService.getAll,
  });
}

export function useSurgeryCentersAdmin() {
  return useQuery({
    queryKey: ["admin", "surgery-centers"],
    queryFn: surgeryCentersLabsService.getSurgeryCenters,
  });
}

export function useLabsAdmin() {
  return useQuery({
    queryKey: ["admin", "labs"],
    queryFn: surgeryCentersLabsService.getLabs,
  });
}

function invalidateCreatedLocationQueries(
  cache: ReturnType<typeof useQueryClient>,
  input: CreateSurgeryCenterLabInput
): void {
  cache.invalidateQueries({ queryKey: ["admin", "surgery-centers-labs"] });
  cache.invalidateQueries({ queryKey: ["admin", "surgery-centers"] });
  cache.invalidateQueries({ queryKey: ["admin", "labs"] });
  if (input.type === "surgery_center") {
    cache.invalidateQueries({ queryKey: surgeryCentersQueryKey, exact: true });
  } else {
    cache.invalidateQueries({ queryKey: labsQueryKey, exact: true });
  }
  cache.invalidateQueries({ queryKey: ["locations"] });
}

function showLocationCreatedNotification(): void {
  notifications.show({
    title: "Success",
    message: "Location created successfully",
    color: "green",
  });
}

function showLocationCreateError(error: Error): void {
  notifications.show({
    title: "Error",
    message: error.message || "Failed to create location",
    color: "red",
  });
}

export function useCreateSurgeryCenterLab() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: CreateSurgeryCenterLabInput) =>
      surgeryCentersLabsService.create(input),
    onSuccess: (_createdLocation, input) => {
      invalidateCreatedLocationQueries(queryClient, input);
      showLocationCreatedNotification();
    },
    onError: (error: Error) => {
      showLocationCreateError(error);
    },
  });
}

export function useUpdateSurgeryCenterLab() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: UpdateSurgeryCenterLabInput) =>
      surgeryCentersLabsService.update(input),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["admin", "surgery-centers-labs"],
      });
      queryClient.invalidateQueries({ queryKey: ["admin", "surgery-centers"] });
      queryClient.invalidateQueries({ queryKey: ["admin", "labs"] });
      notifications.show({
        title: "Success",
        message: "Location updated successfully",
        color: "green",
      });
    },
    onError: (error: Error) => {
      notifications.show({
        title: "Error",
        message: error.message || "Failed to update location",
        color: "red",
      });
    },
  });
}

export function useDeleteSurgeryCenterLab() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => surgeryCentersLabsService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["admin", "surgery-centers-labs"],
      });
      queryClient.invalidateQueries({ queryKey: ["admin", "surgery-centers"] });
      queryClient.invalidateQueries({ queryKey: ["admin", "labs"] });
      notifications.show({
        title: "Success",
        message: "Location deleted successfully",
        color: "green",
      });
    },
    onError: (error: Error) => {
      notifications.show({
        title: "Error",
        message: error.message || "Failed to delete location",
        color: "red",
      });
    },
  });
}

export function useToggleSurgeryCenterLabActive() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) =>
      surgeryCentersLabsService.toggleActive(id, isActive),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["admin", "surgery-centers-labs"],
      });
      queryClient.invalidateQueries({ queryKey: ["admin", "surgery-centers"] });
      queryClient.invalidateQueries({ queryKey: ["admin", "labs"] });
      notifications.show({
        title: "Success",
        message: "Status updated successfully",
        color: "green",
      });
    },
    onError: (error: Error) => {
      notifications.show({
        title: "Error",
        message: error.message || "Failed to update status",
        color: "red",
      });
    },
  });
}

// ── Physician link hooks ─────────────────────────────────────────────────────

export function useSurgeryCenterPhysicians(surgeryCenterLabId: string | null) {
  return useQuery({
    queryKey: [
      "admin",
      "surgery-centers-labs",
      surgeryCenterLabId,
      "physicians",
    ],
    queryFn: () => surgeryCentersLabsService.getPhysicians(surgeryCenterLabId!),
    enabled: !!surgeryCenterLabId,
  });
}

export function useAddSurgeryCenterPhysician() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      surgeryCenterLabId,
      input,
    }: {
      surgeryCenterLabId: string;
      input: { physicianId: string; isPrimary?: boolean };
    }) => surgeryCentersLabsService.addPhysician(surgeryCenterLabId, input),
    onSuccess: (_, { surgeryCenterLabId }) => {
      queryClient.invalidateQueries({
        queryKey: [
          "admin",
          "surgery-centers-labs",
          surgeryCenterLabId,
          "physicians",
        ],
      });
      notifications.show({
        title: "Success",
        message: "Physician linked successfully",
        color: "green",
      });
    },
    onError: (error: Error) => {
      notifications.show({
        title: "Error",
        message: error.message || "Failed to link physician",
        color: "red",
      });
    },
  });
}

export function useSetPrimarySurgeryCenterPhysician() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      surgeryCenterLabId,
      physicianId,
    }: {
      surgeryCenterLabId: string;
      physicianId: string;
    }) =>
      surgeryCentersLabsService.setPrimaryPhysician(
        surgeryCenterLabId,
        physicianId
      ),
    onSuccess: (_, { surgeryCenterLabId }) => {
      queryClient.invalidateQueries({
        queryKey: [
          "admin",
          "surgery-centers-labs",
          surgeryCenterLabId,
          "physicians",
        ],
      });
      notifications.show({
        title: "Success",
        message: "Primary physician updated",
        color: "green",
      });
    },
    onError: (error: Error) => {
      notifications.show({
        title: "Error",
        message: error.message || "Failed to set primary physician",
        color: "red",
      });
    },
  });
}

export function useRemoveSurgeryCenterPhysician() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      surgeryCenterLabId,
      physicianId,
    }: {
      surgeryCenterLabId: string;
      physicianId: string;
    }) =>
      surgeryCentersLabsService.removePhysician(
        surgeryCenterLabId,
        physicianId
      ),
    onSuccess: (_, { surgeryCenterLabId }) => {
      queryClient.invalidateQueries({
        queryKey: [
          "admin",
          "surgery-centers-labs",
          surgeryCenterLabId,
          "physicians",
        ],
      });
      // Also invalidate physician linked surgery centers if that query exists
      queryClient.invalidateQueries({
        queryKey: ["admin", "physicians"],
      });
      notifications.show({
        title: "Success",
        message: "Physician unlinked successfully",
        color: "green",
      });
    },
    onError: (error: Error) => {
      notifications.show({
        title: "Error",
        message: error.message || "Failed to unlink physician",
        color: "red",
      });
    },
  });
}

// ── Report delivery method hooks ─────────────────────────────────────────────

export function useSurgeryCenterReportDeliveryMethods(
  surgeryCenterLabId: string | null
) {
  return useQuery({
    queryKey: [
      "admin",
      "surgery-centers-labs",
      surgeryCenterLabId,
      "report-delivery-methods",
    ],
    queryFn: () =>
      surgeryCentersLabsService.getReportDeliveryMethods(surgeryCenterLabId!),
    enabled: !!surgeryCenterLabId,
  });
}

export function useAddSurgeryCenterReportDeliveryMethod() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      surgeryCenterLabId,
      input,
    }: {
      surgeryCenterLabId: string;
      input: CreateSurgeryCenterLabReportDeliveryMethodInput;
    }) =>
      surgeryCentersLabsService.addReportDeliveryMethod(
        surgeryCenterLabId,
        input
      ),
    onSuccess: (_, { surgeryCenterLabId }) => {
      queryClient.invalidateQueries({
        queryKey: [
          "admin",
          "surgery-centers-labs",
          surgeryCenterLabId,
          "report-delivery-methods",
        ],
      });
      notifications.show({
        title: "Success",
        message: "Delivery method added successfully",
        color: "green",
      });
    },
    onError: (error: Error) => {
      if (isNoPortalAccessError(error)) {
        return;
      }
      notifications.show({
        title: "Error",
        message: error.message || "Failed to add delivery method",
        color: "red",
      });
    },
  });
}

export function useUpdateSurgeryCenterReportDeliveryMethod() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      surgeryCenterLabId,
      input,
    }: {
      surgeryCenterLabId: string;
      input: UpdateSurgeryCenterLabReportDeliveryMethodInput;
    }) =>
      surgeryCentersLabsService.updateReportDeliveryMethod(
        surgeryCenterLabId,
        input
      ),
    onSuccess: (_, { surgeryCenterLabId }) => {
      queryClient.invalidateQueries({
        queryKey: [
          "admin",
          "surgery-centers-labs",
          surgeryCenterLabId,
          "report-delivery-methods",
        ],
      });
      notifications.show({
        title: "Success",
        message: "Delivery method updated successfully",
        color: "green",
      });
    },
    onError: (error: Error) => {
      if (isNoPortalAccessError(error)) {
        return;
      }
      notifications.show({
        title: "Error",
        message: error.message || "Failed to update delivery method",
        color: "red",
      });
    },
  });
}

export function useRemoveSurgeryCenterReportDeliveryMethod() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      surgeryCenterLabId,
      id,
    }: {
      surgeryCenterLabId: string;
      id: string;
    }) =>
      surgeryCentersLabsService.removeReportDeliveryMethod(
        surgeryCenterLabId,
        id
      ),
    onSuccess: (_, { surgeryCenterLabId }) => {
      queryClient.invalidateQueries({
        queryKey: [
          "admin",
          "surgery-centers-labs",
          surgeryCenterLabId,
          "report-delivery-methods",
        ],
      });
      notifications.show({
        title: "Success",
        message: "Delivery method removed successfully",
        color: "green",
      });
    },
    onError: (error: Error) => {
      notifications.show({
        title: "Error",
        message: error.message || "Failed to remove delivery method",
        color: "red",
      });
    },
  });
}

export function useToggleSurgeryCenterReportDeliveryMethodActive() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      surgeryCenterLabId,
      id,
    }: {
      surgeryCenterLabId: string;
      id: string;
    }) =>
      surgeryCentersLabsService.toggleReportDeliveryMethodActive(
        surgeryCenterLabId,
        id
      ),
    onSuccess: (_, { surgeryCenterLabId }) => {
      queryClient.invalidateQueries({
        queryKey: [
          "admin",
          "surgery-centers-labs",
          surgeryCenterLabId,
          "report-delivery-methods",
        ],
      });
      notifications.show({
        title: "Success",
        message: "Status updated successfully",
        color: "green",
      });
    },
    onError: (error: Error) => {
      notifications.show({
        title: "Error",
        message: error.message || "Failed to update status",
        color: "red",
      });
    },
  });
}
